import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Image as RNImage, Platform, View } from 'react-native';
import { Image as ExpoImage } from 'expo-image';
import { SvgUri } from 'react-native-svg';
import { isSvgImageSource } from '@/constants/utils/svg_image';
import { resolveFullSourceUri, resolvePreviewUri } from '@/constants/utils/media_preview';
import { useOnScreen } from '@/hooks/useOnScreen';
import NativeSvgPreview from './NativeSvgPreview';

function mapContentFitToObjectFit(contentFit) {
  if (contentFit === 'cover') return 'cover';
  if (contentFit === 'fill') return 'fill';
  return 'contain';
}

function mapContentFitToPreserveAspectRatio(contentFit) {
  if (contentFit === 'cover') return 'xMidYMid slice';
  if (contentFit === 'fill') return 'none';
  return 'xMidYMid meet';
}

function usesWebImageTag() {
  // Only web has a DOM. macOS is a native platform and must use SvgUri;
  // rendering <img> there throws "View config getter callback for component `img`".
  return Platform.OS === 'web';
}

function MediaImagePreviewSource({
  uri,
  file = null,
  style,
  contentFit = 'cover',
  placeholder,
  transition,
  preferThumbnail = false,
  lazy = true,
  onLoadStart,
  onLoadEnd,
  onError,
}) {
  const [hostRef, isOnScreen] = useOnScreen({ eager: !lazy });
  const vectorUri = resolveFullSourceUri(uri, file);
  const isSvg = isSvgImageSource(file || vectorUri);
  const [layout, setLayout] = useState({ width: 0, height: 0 });
  const [useThumbnailFallback, setUseThumbnailFallback] = useState(false);
  const [bitmapSize, setBitmapSize] = useState(null);
  const activeRef = useRef(true);
  const callbacksRef = useRef(null);
  callbacksRef.current = { onLoadStart, onLoadEnd, onError };
  useEffect(() => {
    activeRef.current = true;
    return () => { activeRef.current = false; };
  }, []);

  const previewUri = useMemo(
    () => resolvePreviewUri({ uri, file, preferThumbnail, isSvg, useThumbnailFallback }),
    [uri, file, preferThumbnail, isSvg, useThumbnailFallback],
  );

  useEffect(() => {
    if (Platform.OS !== 'macos' || !isOnScreen || !previewUri || (isSvg && !useThumbnailFallback)) return;
    let active = true;
    RNImage.getSize(previewUri, (width, height) => {
      if (active && width > 0 && height > 0) setBitmapSize({ width, height });
    }, () => {});
    return () => { active = false; };
  }, [previewUri, isOnScreen, isSvg, useThumbnailFallback]);

  // RCTImageView on Mac can stretch its bitmap to the host frame despite a
  // contain/cover resize mode. Give it a frame with the source aspect ratio.
  let bitmapStyle = { width: '100%', height: '100%' };
  if (Platform.OS === 'macos' && bitmapSize && layout.width > 0 && layout.height > 0 && contentFit !== 'fill') {
    const ratios = [layout.width / bitmapSize.width, layout.height / bitmapSize.height];
    const scale = contentFit === 'none' ? 1 : contentFit === 'cover' ? Math.max(...ratios)
      : Math.min(...ratios, contentFit === 'scale-down' ? 1 : Infinity);
    bitmapStyle = { width: bitmapSize.width * scale, height: bitmapSize.height * scale };
  }

  const handleLayout = useCallback((event) => {
    const { width, height } = event.nativeEvent.layout;
    setLayout({ width, height });
  }, []);

  // SvgUri fetches again when its callbacks change. Keep these stable even
  // when a parent supplies inline loading handlers, and ignore replaced loads.
  const handleLoadStart = useCallback(event => {
    if (activeRef.current) callbacksRef.current.onLoadStart?.(event);
  }, []);
  const handleLoadEnd = useCallback(event => {
    if (activeRef.current) callbacksRef.current.onLoadEnd?.(event);
  }, []);
  const handleImageError = useCallback((event) => {
    if (!activeRef.current) return;
    if (isSvg && file?.thumbnailPath && !useThumbnailFallback) {
      setUseThumbnailFallback(true);
      return;
    }
    callbacksRef.current.onError?.(event);
    // SvgUri has no onLoadEnd event when its fetch fails.
    if (isSvg) callbacksRef.current.onLoadEnd?.(event);
  }, [isSvg, file?.thumbnailPath, useThumbnailFallback]);

  const rendersSvg = !!(isOnScreen && previewUri && isSvg && !useThumbnailFallback
    && (usesWebImageTag() || (layout.width > 0 && layout.height > 0)));
  useEffect(() => {
    if (rendersSvg) handleLoadStart();
  }, [rendersSvg, previewUri, handleLoadStart]);

  let inner = null;
  if (isOnScreen && previewUri) {
    if (isSvg && !useThumbnailFallback) {
      if (usesWebImageTag()) {
        const objectFit = mapContentFitToObjectFit(contentFit);
        inner = (
          <img
            src={previewUri}
            alt=""
            onLoad={handleLoadEnd}
            onError={handleImageError}
            style={{
              width: '100%',
              height: '100%',
              objectFit,
              display: 'block',
            }}
          />
        );
      } else if (layout.width > 0 && layout.height > 0) {
        const SvgPreview = Platform.OS === 'macos' ? NativeSvgPreview : SvgUri;
        inner = (
          <SvgPreview
            uri={previewUri}
            width={layout.width}
            height={layout.height}
            preserveAspectRatio={mapContentFitToPreserveAspectRatio(contentFit)}
            onError={handleImageError}
            onLoad={handleLoadEnd}
          />
        );
      }
    } else {
      inner = (
        <ExpoImage
          source={{ uri: previewUri }}
          style={bitmapStyle}
          contentFit={contentFit}
          placeholder={placeholder}
          transition={transition}
          recyclingKey={previewUri}
          cachePolicy={preferThumbnail ? 'disk' : 'memory-disk'}
          onLoadStart={handleLoadStart}
          onLoadEnd={handleLoadEnd}
          onError={handleImageError}
        />
      );
    }
  }

  return (
    <View ref={hostRef} style={[style, Platform.OS === 'macos' && { alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }]} onLayout={handleLayout}>
      {inner}
    </View>
  );
}

function MediaImagePreview(props) {
  const uri = resolveFullSourceUri(props.uri, props.file);
  const key = JSON.stringify([uri, props.file?.thumbnailPath, isSvgImageSource(props.file || uri)]);
  // A recycled cell must attempt the new original and discard old SVG XML,
  // loading callbacks and thumbnail-fallback state immediately.
  return <MediaImagePreviewSource key={key} {...props} />;
}

export default React.memo(MediaImagePreview);
